import { FC, useEffect, useRef } from 'react'
import ApexCharts, { ApexOptions } from 'apexcharts'

type Props = {
    projectsByRoofType: Array<{ _id: string; count: number }>
}

export const ProjectsByRoofTypeChart: FC<Props> = ({ projectsByRoofType }) => {
    const chartRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        if (!chartRef.current || projectsByRoofType.length === 0) {
            return
        }

        const categories = projectsByRoofType.map((r) => r._id || 'Unknown')
        const data = projectsByRoofType.map((r) => r.count)
        const chart = new ApexCharts(chartRef.current, getChartOptions(categories, data))
        chart.render()

        return () => {
            chart.destroy()
        }
    }, [projectsByRoofType])

    if (projectsByRoofType.length === 0) {
        return (
            <div style={{ height: '240px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8' }}>
                No roof type data available
            </div>
        )
    }

    return <div ref={chartRef} style={{ height: '240px' }}></div>
}

function getChartOptions(categories: string[], data: number[]): ApexOptions {
    return {
        series: [{ name: 'Projects', data }],
        chart: {
            fontFamily: "'Hanken Grotesk', sans-serif",
            type: 'bar',
            height: 240,
            toolbar: { show: false },
            animations: { enabled: true, speed: 600 },
        },
        plotOptions: {
            bar: {
                horizontal: true,
                borderRadius: 5,
                barHeight: '62%',
                dataLabels: { position: 'top' },
            },
        },
        dataLabels: {
            enabled: true,
            offsetX: -14,
            style: {
                fontSize: '12px',
                fontFamily: "'Archivo', sans-serif",
                fontWeight: 700,
                colors: ['#ffffff'],
            },
        },
        stroke: { show: false },
        xaxis: {
            categories,
            labels: {
                style: {
                    colors: '#94a3b8',
                    fontSize: '12px',
                    fontFamily: "'Archivo', sans-serif",
                },
            },
            axisBorder: { show: false },
            axisTicks: { show: false },
        },
        yaxis: {
            labels: {
                style: {
                    colors: '#334155',
                    fontSize: '13px',
                    fontFamily: "'Hanken Grotesk', sans-serif",
                    fontWeight: 500,
                },
            },
        },
        fill: {
            type: 'gradient',
            gradient: {
                shade: 'dark',
                type: 'horizontal',
                shadeIntensity: 0.25,
                gradientToColors: ['#0284c7'],
                inverseColors: false,
                opacityFrom: 1,
                opacityTo: 1,
                stops: [0, 100],
            },
        },
        colors: ['#0f172a'],
        grid: {
            borderColor: '#eef1f5',
            strokeDashArray: 4,
            xaxis: { lines: { show: true } },
            yaxis: { lines: { show: false } },
        },
        tooltip: {
            style: {
                fontSize: '13px',
                fontFamily: "'Hanken Grotesk', sans-serif",
            },
            y: {
                formatter: (val) => `${val} projects`,
            },
        },
    }
}
